import _ from "lodash";
import { PricingRequest } from "../components/OverviewStep";
import {
	PricingRequestAPI,
	Granularity,
} from "../pricingRequestStepper.schema";
import { format } from "date-fns";
import { Portfolio, Site, isPortfolio } from "../../sites/sites.module";
import { Tender } from "../../tender_page/tender.module";
import { notNullLike } from "../../../common/utils/notNullLike";
import {
	PricingRequestData,
	PricingRequestDataKeys,
} from "../pricingRequestStepper.module";
import { CommodityFixings } from "../PricingRequestStepper.slice";
import { FinancialType } from "../PricingRequestStepper.constant";

const granularity: Granularity[] = ["MONTH", "QUARTER", "CAL", "WHOLE_HORIZON"];

function getGranularitiesBasedOnFlags(
	pricing: PricingRequestData
): Granularity[] {
	return granularity.filter(function (elem: string, index: number) {
		const flags: Boolean[] = [
			pricing.granularityMonthly,
			pricing.granularityQuarter,
			pricing.granularityCal,
			pricing.granularityWholeHorizon,
		];
		return flags[index];
	});
}

export function getPortfoliosFromPricingRequestForm(
	sitesValues: (Portfolio | Site)[]
) {
	return sitesValues.filter(
		(portfolio: Portfolio | Site) => "sites" in portfolio && portfolio.sites
	);
}

export function getSitesFromPricingRequestForm(
	sitesValues: (Portfolio | Site)[]
) {
	return sitesValues.filter((site: Portfolio | Site) => !("sites" in site));
}

export function transformPricingRequestDataToAdaptApi(
	pricingRequest: PricingRequest,
	tenderId: number,
	isFinancial: boolean = false
): PricingRequestAPI {
	const pricing = pricingRequest.pricingValues;
	const sites = pricingRequest.sitesValues;
	const negativePrice = {
		negative_price_value: pricing.negativePriceValue,
		negative_price_index: pricing.negativePriceIndex,
		negative_price_threshold: pricing.negativePriceThreshold,
		negative_price_operator: pricing.negativePriceOperator,
		negative_price_nb_consecutive_blocks:
			pricing.negativePriceNBConsecutiveBlocks,
	};
	const curtailabilityForward = {
		curtailability_pricing_index: pricing.curtailabilityForwardIndex,
		curtailability_forward_threshold:
			pricing.curtailabilityForwardThreshold,
		curtailability_forward_operator: pricing.curtailabilityForwardOperator,
		curtailability_price_nb_consecutive_blocks:
			pricing.curtailabilityForwardNBConsecutiveBlocks,
	};
	
	const capFloor = {
		payout_method: pricing.payoutMethod,
		cap_value: pricing.cap !== "" ? pricing.cap : undefined,
		floor_value: pricing.floor !== "" ? pricing.floor : undefined
	};

	const capFloorIntraDay = {
		payout_method_intraday: pricing.payoutMethodIntraDay,
		cap_value_intraday: pricing.capIntraDay !== "" ? pricing.capIntraDay : undefined,
		floor_value_intraday: pricing.floorIntraDay !== "" ? pricing.floorIntraDay : undefined
	};

	let mapped: PricingRequestAPI = {
		start_date: format(pricing.startDate, "yyyy-MM-dd"),
		end_date: format(pricing.endDate, "yyyy-MM-dd"),
		alpha: pricing.alpha,
		beta: pricing.beta,
		alpha_and_beta: pricing.alphaAndBeta,
		pricing_date: pricing.live
			? undefined
			: pricing.pricingDate
			? format(pricing.pricingDate, "yyyy-MM-dd")
			: undefined,
		is_real_time: pricing.live,
		granularities: getGranularitiesBasedOnFlags(pricing),
		clickability: pricing.clickOptionSwitch
			? pricing.clickGranularity.map((item) => {
					const clickKey =
						`clickCount${item}` as PricingRequestDataKeys;
					return {
						granularity: item,
						click_count: Number(pricing[clickKey]),
					};
			  })
			: undefined,
		tender_id: tenderId,
		site_ids: getSitesFromPricingRequestForm(sites).map((el) => el.id),
		portfolio_ids: getPortfoliosFromPricingRequestForm(sites).map(
			(el) => el.id
		),
		types: _.isEmpty(pricing.pricingType)
			? [pricing.strikePricingType as CommodityFixings]
			: pricing.pricingType,
		base_capacity_volume_percentage: Number(pricing.baseCapacity) / 100,
		chapter51_type: pricing.chapter51Switch
			? pricing.chapter51Type
			: undefined,
		chapter51_number_of_hours: pricing.chapter51Switch
			? pricing.chapter51NumberOfHours
			: undefined,

		ppa_with_goo: pricing.ppaWithGooSwitch,
		goo_power_adjustment: Number(pricing.gooPowerAdjustment) / 100,
		// TODO for now it is always false
		only_goo: false,
		is_financial: isFinancial,
		commodity_pricing_type: pricing.commodityPricingType,
		strike_pricing_type: pricing.strikePricingType,
		average_type: pricing.averageType,

		commodity_beta: pricing.commodityBeta,
		commodity_alpha: pricing.commodityAlpha,
		commodity_has_negative_price: pricing.commodityHasNegativePrice,
		strike_has_negative_price: pricing.strikeHasNegativePrice,
		has_base_options: pricing.hasBaseOptions,
		leg_type: pricing.legType,
		is_nodal: pricing.isNodal,
		apply_set_up_fees: Boolean(pricing?.applySetupFees)
	};
	if (pricing.hasPayoutMethod) {
		mapped = {
			...mapped,
			...capFloor
		}
	}

	if (pricing.hasPayoutMethodIntraDay) {
		mapped = {
			...mapped,
			...capFloorIntraDay
		}
	}

	if (pricing.hasNegativePrice || pricing.strikeHasNegativePrice || pricing.commodityHasNegativePrice) {
		mapped = {
			...mapped,
			...negativePrice,
		};
	}

	if (pricing.hasCurtailabilityForward) {
		mapped = {
			...mapped,
			...curtailabilityForward,
		};
	}

	return mapped;
}

const deepCopyAndSortPortfolioChildren = (portfolio: Portfolio): Portfolio => {
	const childrenSite = _.cloneDeep(portfolio.children);
	const sortedChildren = _.sortBy(childrenSite, (site) => site.name);

	// changing children directly will trigger an error Cannot Assign to Read Only
	return { ...portfolio, children: sortedChildren };
};

export function mergeSitesAndPortfolios(tender?: Tender) {
	if (!tender) {
		return [];
	}
	const portfolios = (tender.portfolios || [])
		.map((portfolio) => ({
			...portfolio,
			children: portfolio.sites || [],
		}))
		.filter(notNullLike)
		.filter((portfolio) => !portfolio.is_portfolio_all);
	return [...portfolios, ...(tender?.sites || [])];
}

export const sortSitesOrPortfoliosByNameAscending = (
	sitesOrPortfolios: (Site | Portfolio)[]
) => {
	const sortedSiteOrPortfolio = _.sortBy(
		sitesOrPortfolios,
		(siteOrPortoflio) => siteOrPortoflio.name
	);
	const [portfolios, sites] = _.partition(
		sortedSiteOrPortfolio,
		(siteOrPortfolio: Site | Portfolio) => isPortfolio(siteOrPortfolio)
	);
	const sortedPortfolio = portfolios.map((portfolio) =>
		deepCopyAndSortPortfolioChildren(portfolio as Portfolio)
	);
	// @ts-ignore
	return sortedPortfolio.concat(sites);
};

export const resetDateHours = (date: Date) =>
	date ? new Date(date.setHours(0, 0, 0, 0)) : date;

export const isFinancialClickPricing = (pricingTypeName: FinancialType) => {
	if (!!pricingTypeName) {
		return [
			FinancialType.CLICK_FINANCIAL,
			FinancialType.CPPA_CLICK_FINANCIAL,
		].includes(pricingTypeName);
	}
	return pricingTypeName;
};

export const isFinancialDAPricing = (pricingTypeName: FinancialType) => {
	return [
		FinancialType.DA_FINANCIAL,
		FinancialType.CPPA_DA_FINANCIAL,
	].includes(pricingTypeName);
};

export const isFinancialFixedPricing = (pricingTypeName: FinancialType) => {
	return [
		FinancialType.FIXED_FINANCIAL,
		FinancialType.CPPA_FIXED_FINANCIAL,
	].includes(pricingTypeName);
};

export function allSameCountry(items: (Site | Portfolio)[]) {
	if (!items || items.length === 0) return true;

	// Helper to recursively get all country_iso2_code values
	function extractCountries(obj: Site | Portfolio) {
		let countries = [];

		if ("country_iso2_code" in obj && obj.country_iso2_code) {
			countries.push(obj.country_iso2_code);
		}

		// If it has sites or tender_sites, check those too
		if ("sites" in obj && obj.sites && obj.sites.length > 0) {
			for (const site of obj.sites) {
				countries.push(site.country_iso2_code);
			}
		}

		return countries;
	}

	// Extract all country codes from all top-level items
	const allCountries = items.flatMap(extractCountries);

	// Check if all country codes are identical
	return allCountries.every((code) => code === allCountries[0]);
}
